import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2, X } from 'lucide-react';
import type { P2pProvider } from '../sync/p2pProvider';
import { Button } from '../components/Button';

interface Props {
  provider: P2pProvider;
  // Raw offer code lifted out of the URL hash.
  offerCode: string;
  // Called when the user dismisses the flow (success or cancel) so the
  // parent can clear the hash so a refresh doesn't reopen the dialog.
  onDismiss: () => void;
}

type Phase =
  | { kind: 'preparing' }
  | { kind: 'awaiting-approval'; answerCode: string }
  | { kind: 'connected' }
  | { kind: 'error'; message: string };

// Modal-ish overlay shown when a guest opens an invite URL. Walks them
// through:
//   1. Generate an answer code (automatically on mount)
//   2. Copy it and send to the inviter
//   3. Wait for the data channel to open (provider emits peers-changed)
//
// The flow shares the BoardPage's existing P2pProvider, so once the
// connection is approved the canvas behind the overlay is already wired up
// and there's no remount or re-handshake.
export function JoinFlow({ provider, offerCode, onDismiss }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'preparing' });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void respond();

    async function respond() {
      try {
        const { answerCode } = await provider.respondToInvite(offerCode);
        setPhase({ kind: 'awaiting-approval', answerCode });
      } catch (e) {
        setPhase({ kind: 'error', message: messageOf(e) });
      }
    }
  }, [provider, offerCode]);

  useEffect(() => {
    // Watch for the data channel opening — that's the signal the inviter
    // has approved our answer code.
    const onPeers = () => {
      if (provider.listPeers().length > 0) {
        setPhase({ kind: 'connected' });
      }
    };
    provider.on('peers-changed', onPeers);
    onPeers();
    return () => provider.off('peers-changed', onPeers);
  }, [provider]);

  // Auto-dismiss a couple seconds after a successful join so the user can
  // see the confirmation without being trapped behind a modal.
  useEffect(() => {
    if (phase.kind !== 'connected') return;
    const t = window.setTimeout(onDismiss, 1500);
    return () => window.clearTimeout(t);
  }, [phase.kind, onDismiss]);

  return (
    <div className="absolute inset-0 z-[5] bg-bg-app/70 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-[400px] max-w-full bg-bg-panel border border-border-strong rounded-3 shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
          <strong className="text-13 font-medium text-fg-primary">Joining live board</strong>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Cancel"
            className="w-6 h-6 inline-flex items-center justify-center rounded-2 text-fg-tertiary hover:bg-bg-hover hover:text-fg-primary"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </header>
        <div className="px-4 py-4 flex flex-col gap-3">
          {phase.kind === 'preparing' && <Preparing />}
          {phase.kind === 'awaiting-approval' && <AwaitingApproval code={phase.answerCode} />}
          {phase.kind === 'connected' && <Connected />}
          {phase.kind === 'error' && <ErrorView message={phase.message} />}
        </div>
      </div>
    </div>
  );
}

function Preparing() {
  return (
    <div className="flex items-center gap-2 text-13 text-fg-secondary">
      <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
      Preparing connection…
    </div>
  );
}

function AwaitingApproval({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  }, [code]);
  return (
    <>
      <p className="text-13 text-fg-secondary leading-[1.5] m-0">
        Send this code back to whoever invited you. The board will connect as soon as they paste
        it in.
      </p>
      <textarea
        readOnly
        value={code}
        onFocus={(e) => e.currentTarget.select()}
        rows={4}
        className="
          w-full px-2.5 py-1.5
          bg-bg-subtle border border-border-default rounded-2
          font-mono text-12 text-fg-primary
          focus-visible:outline-none focus-visible:shadow-focus
          resize-none
        "
      />
      <Button variant="primary" size="sm" onClick={copy}>
        {copied ? (
          <>
            <Check size={14} strokeWidth={1.5} />
            Copied
          </>
        ) : (
          <>
            <Copy size={14} strokeWidth={1.5} />
            Copy code
          </>
        )}
      </Button>
      <p className="text-12 text-fg-tertiary leading-[1.4] m-0">
        Waiting for them to approve…
      </p>
    </>
  );
}

function Connected() {
  return (
    <div className="flex items-center gap-2 text-13 text-success">
      <Check size={14} strokeWidth={1.5} />
      Connected.
    </div>
  );
}

function ErrorView({ message }: { message: string }) {
  return (
    <p className="text-13 text-error leading-[1.5] m-0" role="alert">
      {message}
    </p>
  );
}

function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  return 'Could not parse invite code.';
}
